import produce, { Draft } from "immer";
import { v4 } from "uuid";

import { force } from "../../typeguards/force";
import { Action, CoverageInsuranceType, DlpState, InsuredPerson } from "./DlpFlow.interfaces";

export const initialState: DlpState = {
  insuredPersons: [{ isDefault: true, personId: v4() }],
  hasSecondInsured: undefined,
  coverages: [],
  paymentTerm: "Monthly",
  crosswiseInsured: undefined,
  hasMortgage: "No",
  insuranceTaker: [],
  mortgageSupplierKey: undefined,
  mortgageSupplierLabel: undefined,
};

const updateHasMortgage = (action: { payload: { hasMortgage: boolean } }, draft: Draft<DlpState>) => {
  const yesOrNo = action.payload.hasMortgage ? "Yes" : "No";
  draft.hasMortgage = yesOrNo;
  if (!action.payload.hasMortgage) {
    draft.mortgageSupplierKey = undefined;
    draft.mortgageSupplierLabel = undefined;
  }
};

const updateCoverageReductionRate = (draft: Draft<DlpState>, index: number, reductionRate?: number | undefined) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].reductionRate = reductionRate;
  }
};

const updateCoverageInsuranceType = (draft: Draft<DlpState>, index: number, insuranceType: CoverageInsuranceType) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].insuranceType = insuranceType;
    if (insuranceType !== "Annuity") {
      draft.coverages[index].reductionRate = undefined;
    }
  }
};

const updateCoverageEffectiveDate = (draft: Draft<DlpState>, index: number, effectiveDate?: string | undefined) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].effectiveDate = effectiveDate;
    draft.coverages[index].hasEffectiveDate = true;
  }
};

const updateCoverageHasDuration = (draft: Draft<DlpState>, index: number) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].hasDuration = true;
  }
};

const updateCoverageDuration = (
  draft: Draft<DlpState>,
  index: number,
  duration: number | undefined,
  hasDuration: boolean
) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].duration = duration;
    if (hasDuration) {
      draft.coverages[index].hasDuration = true;
    }
  }
};

const updateHasInsuredAmount = (draft: Draft<DlpState>, index: number) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].hasInsuredAmount = true;
  }
};

const updateEffectiveInsured = (draft: Draft<DlpState>, index: number, effectiveInsured: string[]) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].effectiveInsured = effectiveInsured;
  }
};

const deleteCoverage = (draft: Draft<DlpState>, index: number) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages.splice(index, 1);
  }
};

const updateInsuredData = (
  draft: Draft<DlpState>,
  action: { payload: { index: number; data: Partial<InsuredPerson> } }
) => {
  const insuredPerson = draft.insuredPersons[action.payload.index];
  if (insuredPerson) {
    draft.insuredPersons[action.payload.index] = { ...insuredPerson, ...action.payload.data };
  }
};

const updateCoverageInsuredAmount = (
  draft: Draft<DlpState>,
  index: number,
  insuredAmount: number | undefined,
  hasInsuredAmount: boolean
) => {
  const coverage = draft.coverages[index];
  if (coverage) {
    draft.coverages[index].insuredAmount = insuredAmount;
    if (hasInsuredAmount) {
      draft.coverages[index].hasInsuredAmount = true;
    }
  }
};

const deleteAndUpdateInsuredPerson = (
  insuredPerson: Draft<Partial<InsuredPerson>>,
  draft: Draft<DlpState>,
  index: number
) => {
  if (insuredPerson && insuredPerson.personId) {
    draft.insuredPersons.splice(index, 1);
    draft.insuranceTaker = [force(draft.insuredPersons[0].personId)];
    draft.coverages.map((c) => {
      if (c.effectiveInsured?.includes(force(insuredPerson.personId))) {
        c.effectiveInsured = [force(draft.insuredPersons[0].personId)];
      }
    });
  }
};

export const dlpFlowStateReducer = (state: DlpState, action: Action): DlpState => {
  switch (action.type) {
    case "REHYDRATE":
      return action.payload.data;
    case "ADD_INSURED_PERSON":
      return produce(state, (draft) => {
        draft.insuredPersons.push({ personId: v4(), isDefault: false });
        draft.hasSecondInsured = "Yes";
      });
    case "DELETE_INSURED_PERSON":
      return produce(state, (draft) => {
        draft.hasSecondInsured = "No";
        const insuredPerson = draft.insuredPersons[action.payload.index];

        deleteAndUpdateInsuredPerson(insuredPerson, draft, action.payload.index);
      });
    case "UPDATE_INSURED_DATA":
      return produce(state, (draft) => {
        updateInsuredData(draft, action);
      });
    case "ADD_COVERAGE":
      return produce(state, (draft) => {
        draft.coverages.push({ coverageId: v4(), effectiveInsured: action.payload.effectiveInsured });
      });
    case "DELETE_COVERAGE":
      return produce(state, (draft) => {
        deleteCoverage(draft, action.payload.index);
      });
    case "UPDATE_COVERAGE_EFFECTIVE_INSURED":
      return produce(state, (draft) => {
        updateEffectiveInsured(draft, action.payload.index, action.payload.effectiveInsured);
      });
    case "UPDATE_COVERAGE_INSURED_AMOUNT":
      return produce(state, (draft) => {
        updateCoverageInsuredAmount(
          draft,
          action.payload.index,
          action.payload.insuredAmount,
          action.payload.hasInsuredAmount
        );
      });
    case "UPDATE_COVERAGE_HAS_INSURED_AMOUNT":
      return produce(state, (draft) => {
        updateHasInsuredAmount(draft, action.payload.index);
      });
    case "UPDATE_COVERAGE_DURATION":
      return produce(state, (draft) => {
        updateCoverageDuration(draft, action.payload.index, action.payload.duration, action.payload.hasDuration);
      });
    case "UPDATE_COVERAGE_HAS_DURATION":
      return produce(state, (draft) => {
        updateCoverageHasDuration(draft, action.payload.index);
      });
    case "UPDATE_COVERAGE_EFFECTIVE_DATE":
      return produce(state, (draft) => {
        updateCoverageEffectiveDate(draft, action.payload.index, action.payload.effectiveDate);
      });
    case "UPDATE_COVERAGE_INSURANCE_TYPE":
      return produce(state, (draft) => {
        updateCoverageInsuranceType(draft, action.payload.index, action.payload.insuranceType);
      });
    case "UPDATE_COVERAGE_REDUCTION_RATE":
      return produce(state, (draft) => {
        updateCoverageReductionRate(draft, action.payload.index, action.payload.reductionRate);
      });
    case "UPDATE_INSURANCE_TAKER":
      return produce(state, (draft) => {
        draft.insuranceTaker = action.payload.insuranceTaker;
      });
    case "UPDATE_CROSSWISE_INSURED":
      return produce(state, (draft) => {
        draft.crosswiseInsured = action.payload.isCrosswiseInsured;
      });
    case "UPDATE_HAS_MORTGAGE":
      return produce(state, (draft) => {
        updateHasMortgage(action, draft);
      });
    case "UPDATE_MORTGAGE_SUPPLIER":
      return produce(state, (draft) => {
        draft.mortgageSupplierKey = action.payload.mortgageSupplierKey;
        draft.mortgageSupplierLabel = action.payload.mortgageSupplierLabel;
      });
    case "UPDATE_PAYMENT_TERM":
      return produce(state, (draft) => {
        draft.paymentTerm = action.payload.paymentTerm;
      });
  }
};
